/**
 * The questions a marketer adds to an event's nomination form.
 *
 * Every question is a prompt and nothing else — no answer type, no option list. The BD
 * side renders each one as a single text input, which is why a question is just its
 * wording. That keeps the shape the event has to store as small as the feature needs.
 *
 * These make up the whole of the nomination form's "Travel & Event Arrangements" section.
 * The mock draws Local / Accommodation / Check-in / Check-out there, but those are an
 * example of what a marketer configured, not fields the form has built in — which is why
 * nothing here is hard-coded. The fixed half of the nomination form is Contact
 * Information, and that is not configurable at all (see NominationFormConfig).
 *
 * Pure, and every function returns a new list.
 */

/** One configured question. */
export interface NominationQuestion {
  /**
   * Identity for editing only: React keys and which row an action targets. Generated in
   * the browser and NOT part of the saved shape — a question IS its wording, so persisting
   * a local key would invite treating it as a server id.
   */
  key: string;
  /** What the BD is asked. Empty while a freshly added row is still blank. */
  prompt: string;
}

let nextKey = 0;

export function newQuestion(prompt = ''): NominationQuestion {
  nextKey += 1;
  return { key: `q${nextKey}`, prompt };
}

export function setPrompt(
  questions: readonly NominationQuestion[],
  key: string,
  prompt: string,
): NominationQuestion[] {
  return questions.map((q) => (q.key === key ? { ...q, prompt } : q));
}

export function removeQuestion(
  questions: readonly NominationQuestion[],
  key: string,
): NominationQuestion[] {
  return questions.filter((q) => q.key !== key);
}

/**
 * The prompts that appear more than once, compared trimmed and case-insensitively.
 *
 * Two identical questions on one form give the BD no way to tell which answer goes where,
 * and give whoever reads the answers two columns meaning the same thing. Reported rather
 * than prevented, so both offenders are visible and the marketer picks which to change.
 */
export function duplicatePrompts(
  questions: readonly NominationQuestion[],
): Set<string> {
  const seen = new Set<string>();
  const dupes = new Set<string>();
  for (const q of questions) {
    const key = q.prompt.trim().toLowerCase();
    if (!key) continue;
    if (seen.has(key)) dupes.add(key);
    seen.add(key);
  }
  return dupes;
}

/**
 * The questions as `Event.nominationForm` stores them: blank rows dropped, prompts trimmed,
 * keys gone — a plain list of strings, in display order.
 *
 * A row the marketer added and left empty is not a question, and saving it would put an
 * unlabelled input in front of every BD who nominates to this event.
 *
 * ORDER IS LOAD-BEARING. A nomination's answers are matched to questions by position
 * (`CreateNominationInput.formAnswers`), so reordering or removing a question would
 * re-point every already-submitted answer. The service prevents that by locking the form
 * once the event is PUBLISHED; this function must not reorder on its own either.
 */
export function questionsForSave(
  questions: readonly NominationQuestion[],
): string[] {
  return questions.map((q) => q.prompt.trim()).filter((prompt) => prompt !== '');
}

/**
 * The editor's rows, seeded from what the event has stored.
 *
 * The inverse of {@link questionsForSave}. Keys are generated here because the stored shape
 * has none — a question IS its wording — and the editor needs something stable to address
 * rows by while the marketer types.
 */
export function questionsFrom(
  prompts: readonly string[] | undefined,
): NominationQuestion[] {
  return (prompts ?? []).map((prompt) => newQuestion(prompt));
}
